import { Outlet, createRootRouteWithContext, useNavigate, useRouterState } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ToastProvider, useToast } from '@/components/ui/toast';
import { SessionTimeoutDialog } from '@/components/session-timeout-dialog';
import { authStore } from '@/lib/auth-store';
import { leaveAfterSignOut } from '@/lib/session-exit';
import { announceSessionEnded, useSessionTimeout } from '@/lib/use-session-timeout';
import { useAuth } from '@/lib/use-auth';

export interface RouterContext {
  queryClient: QueryClient;
  auth: typeof authStore;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

/**
 * Pages anybody may open without an account (the FIRE to IRIS converter). A browser that
 * still holds an old e-filing session must be able to read them: the session watchers below
 * would otherwise see that session end and carry the visitor off to the sign-in page.
 */
const PUBLIC_PATH = /\/tools\//;

function useOnPublicPage(): boolean {
  return useRouterState({ select: (state) => PUBLIC_PATH.test(state.location.pathname) });
}

/** Sends the user back to sign in when Tab Service rejects the session. */
function SessionWatcher() {
  const auth = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const onPublicPage = useOnPublicPage();

  useEffect(() => {
    if (!auth.expired) return;
    authStore.clearExpired();
    // The session is over either way; a public page just carries on without it.
    if (onPublicPage) return;
    leaveAfterSignOut(() => {
      toast.show({
        title: 'Your session ended',
        description: 'Sign in again to continue.',
        tone: 'warning',
      });
      void navigate({ to: '/sign-in' });
    });
  }, [auth.expired, navigate, toast, onPublicPage]);

  return null;
}

/**
 * The idle warning, on every screen behind a session (not on public pages).
 *
 * Signing out from here ends the session on the portal as well, so the minute running out in
 * e-filing leaves the person at the portal's login page, exactly where signing out by hand
 * leaves them.
 */
function IdleWarning() {
  const auth = useAuth();
  const navigate = useNavigate();
  // A public page has no session to keep alive or to time out.
  const onPublicPage = useOnPublicPage();
  const signedIn = auth.status === 'signed-in' && !onPublicPage;

  const end = useCallback(() => {
    announceSessionEnded();
    void authStore.signOut().then(() => {
      leaveAfterSignOut(() => void navigate({ to: '/sign-in' }));
    });
  }, [navigate]);

  const session = useSessionTimeout(signedIn, end);

  if (!signedIn) return null;
  return (
    <SessionTimeoutDialog
      open={session.warning}
      secondsLeft={session.secondsLeft}
      extending={session.extending}
      onExtend={session.extend}
      onSignOut={end}
    />
  );
}

function RootLayout() {
  return (
    <TooltipProvider delayDuration={200} skipDelayDuration={300}>
      <ToastProvider>
        <SessionWatcher />
        <IdleWarning />
        <Outlet />
      </ToastProvider>
    </TooltipProvider>
  );
}
